import ENDPOINTS, { buildUrl } from '.';

import config from '@/config';
import {
  lotteryHistoryResponseSchema,
  lotteryLeaderboardResponseSchema,
  lotterySeasonResponseSchema,
  seasonResultsResponseSchema,
  unclaimedRewardsResponseSchema,
  userNFTsResponseSchema,
  shareResultResponseSchema,
  LotteryHistoryResponse,
  LotteryLeaderboardResponse,
  LotterySeasonResponse,
  SeasonResultsResponse,
  UnclaimedRewardsResponse,
  UserNFTsResponse,
  ShareResultResponse,
} from '@/types/schemas';

export async function getCurrentSeason(walletAddress?: string): Promise<LotterySeasonResponse> {
  const url = buildUrl(ENDPOINTS.pointsLotteryCurrent, {
    wallet_address: walletAddress,
  });

  const response = await fetch(url, {
    credentials: 'include',
  });

  const json = await response.json();
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  // Backend returns {code: 0, data: {...}}, extract data
  const responseData = json.data || json;
  return lotterySeasonResponseSchema.parse(responseData);
}

interface ContributePayload {
  points: number;
}

export async function contributePoints(payload: ContributePayload) {
  const response = await fetch(ENDPOINTS.pointsLotteryContribute, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ points: payload.points }),
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  // Backend returns {code: 0, data: {...}}, extract data
  const responseData = json.data || json;
  return lotterySeasonResponseSchema.parse(responseData);
}

interface HistoryParams {
  season?: number;
  wallet_address?: string;
  [key: string]: string | number | undefined;
}

export async function getHistory(params: HistoryParams = {}): Promise<LotteryHistoryResponse> {
  const url = buildUrl(ENDPOINTS.pointsLotteryHistory, params);

  const response = await fetch(url, {
    credentials: 'include',
  });

  const json = await response.json();
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  // Backend returns {code: 0, data: {...}}, extract data
  const responseData = json.data || json;
  return lotteryHistoryResponseSchema.parse(responseData);
}

interface LeaderboardParams {
  page?: number;
  page_size?: number;
  wallet_address?: string;
  [key: string]: string | number | undefined;
}

export async function getLeaderboard(params: LeaderboardParams = {}): Promise<LotteryLeaderboardResponse> {
  const url = buildUrl(ENDPOINTS.pointsLotteryRank, params);

  const response = await fetch(url, {
    credentials: 'include',
  });

  const json = await response.json();
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  // Backend returns {code: 0, data: {...}}, extract data
  const responseData = json.data || json;
  return lotteryLeaderboardResponseSchema.parse(responseData);
}

interface ClaimRewardPayload {
  season_id?: number;
}

export async function claimLotteryReward(payload: ClaimRewardPayload = {}) {
  const response = await fetch(ENDPOINTS.pointsLotteryClaim, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json;
}

export async function getSeasonResults(seasonId: number): Promise<SeasonResultsResponse> {
  const url = `${ENDPOINTS.pointsLotterySeasonResults}/${seasonId}/results`;

  const response = await fetch(url, {
    credentials: 'include',
  });

  const json = await response.json();

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  const responseData = json.data || json;
  return seasonResultsResponseSchema.parse(responseData);
}

interface MintChestNFTPayload {
  season_no?: number;
}

interface ChestNFTOrderResponse {
  order_id: string;
  user_id: number;
  address: string;
  type: number;
  sub_type: number;
  metadata: {
    season_id: number;
    season_no: number;
    rank: number;
    points: string;
    nftTier: string;
    nftTierUrl: string;
  };
}

/**
 * Step 1: Create NFT mint order using /chest/mint-nft endpoint
 * This uses the simpler rank-based tier calculation (getNftTier)
 */
export async function mintChestNFT(payload: MintChestNFTPayload = {}): Promise<ChestNFTOrderResponse> {
  const requestPayload = {
    season_no: payload.season_no,
  };

  const response = await fetch(ENDPOINTS.chestMintNFT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestPayload),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json.data || json;
}

/**
 * Step 2: Generate signature for the NFT mint transaction
 */
export async function generateSignature(orderId: string): Promise<{ serialized_tx: string }> {
  const url = `${ENDPOINTS.txOrderGenerateSignature}?order_id=${encodeURIComponent(orderId)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json.data || json;
}

interface ConfirmNftMintPayload {
  orderId: string;
  signature: string;
  nftMint: string;
}

export async function confirmNftMint(payload: ConfirmNftMintPayload): Promise<{ mintAddress: string; signature: string; tier: string; rank: number | null }> {
  const response = await fetch(ENDPOINTS.pointsLotteryNFTConfirm, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await response.json();
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json.data || json;
}

export async function getUnclaimedRewards(): Promise<UnclaimedRewardsResponse> {
  const response = await fetch(ENDPOINTS.pointsLotteryNFTUnclaimed, {
    credentials: 'include',
  });

  const json = await response.json();
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  const responseData = json.data || json;
  return unclaimedRewardsResponseSchema.parse(responseData);
}

export async function getUserLotteryNFTs(): Promise<UserNFTsResponse> {
  const response = await fetch(ENDPOINTS.pointsLotteryNFTUser, {
    credentials: 'include',
  });

  const json = await response.json();

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  const responseData = json.data || json;
  return userNFTsResponseSchema.parse(responseData);
}

interface GenerateShareResultPayload {
  seasonId: number;
}

export async function generateShareResult(payload: GenerateShareResultPayload): Promise<ShareResultResponse> {
  const response = await fetch(ENDPOINTS.pointsLotteryShareResult, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  const responseData = json.data || json;
  return shareResultResponseSchema.parse(responseData);
}


/**
 * Verify lottery share tweet and enable claim
 */
interface VerifyAndClaimPayload {
  winnerId: number;
  tweetUrl: string;
}

export async function verifyAndClaimReward(payload: VerifyAndClaimPayload) {
  // In nonprod with bypass enabled, use mock tweet URL if not provided
  const requestPayload = config.ENABLE_NFT_DEBUG_BYPASS && !payload.tweetUrl
    ? { ...payload, tweetUrl: 'https://twitter.com/mock/status/123456789' }
    : payload;

  const response = await fetch(ENDPOINTS.pointsLotteryRewardVerifyAndClaim, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestPayload),
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json.data || json;
}

/**
 * Verify lottery share tweet via Twitter API
 */
export async function verifyLotteryShareTweet() {
  const response = await fetch(ENDPOINTS.twitterVerifyLotteryShare, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json;
}

/**
 * Verify NFT mint tweet via Twitter API
 */
export async function verifyNFTMintTweet() {
  const response = await fetch(ENDPOINTS.twitterVerifyNFTMint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json;
}

/**
 * Generate lottery share image
 */
interface GenerateShareImagePayload {
  winnerId: number;
  seasonId: number;
}

export async function generateLotteryShareImage(payload: GenerateShareImagePayload) {
  const response = await fetch(ENDPOINTS.shareImageLottery, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const json = await response.json().catch(() => ({}));
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  // Get tweet text from headers
  const tweetText = decodeURIComponent(response.headers.get('X-Tweet-Text') || '');

  // Get image as blob
  const imageBlob = await response.blob();
  const imageUrl = URL.createObjectURL(imageBlob);

  return {
    imageUrl,
    tweetText,
  };
}

/**
 * Get tweet text for lottery share (without generating full image)
 */
export async function getLotteryShareTweetText(payload: GenerateShareImagePayload) {
  const response = await fetch(ENDPOINTS.shareImageLotteryTweetText, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json.data || json;
}

/**
 * Get tweet text for NFT minting (without generating image)
 */
interface GenerateNFTTweetPayload {
  seasonId?: number;
}

export async function getNFTMintTweetText(payload: GenerateNFTTweetPayload = {}) {
  const response = await fetch(ENDPOINTS.shareImageNFTTweetText, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  return json.data || json;
}

/**
 * Automatically post lottery share tweet with media
 */
export async function autoPostLotteryTweet(payload: GenerateShareImagePayload) {
  const response = await fetch(ENDPOINTS.shareImageLotteryAutoPost, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  if (json.code !== 0) {
    throw new Error(json.message || 'Failed to post tweet');
  }

  return json.data || json;
}

/**
 * Automatically post NFT mint tweet
 */
export async function autoPostNFTTweet(payload: GenerateNFTTweetPayload = {}) {
  const response = await fetch(ENDPOINTS.shareImageNFTAutoPost, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    throw new Error(message);
  }

  if (json.code !== 0) {
    throw new Error(json.message || 'Failed to post tweet');
  }

  return json.data || json;
}
